import React, { useState } from "react";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { useBulkReviewCompliances } from "../../../../backend/compliance";
import { ComplianceActivityStatus, ComplianceStatusMapping } from "../../../../constants/Compliance.constants";
import './ComplianceBulkModals.css';

const REVIEWABLE_STATUSES = [ComplianceActivityStatus.PENDING];

/* ── Inline SVG icons ─── */
function IconClose() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}
function IconInfo() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    );
}
function IconCheck() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}
function IconX() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

function ComplianceBulkReviewModal({ onClose, selectedItems }: any) {
    const [status, setStatus]           = useState('');
    const [auditRemarks, setAuditRemarks] = useState('');

    const { bulkReviewCompliances, reviewing } = useBulkReviewCompliances(
        function(response: any) {
            onClose(true);
            var failed = ((response || {}).data || {}).failed || [];
            if (failed.length > 0) {
                toast.warning(failed.length + ' compliance record(s) could not be updated.');
            }
            toast.success(
                status === ComplianceActivityStatus.APPROVED
                    ? 'Compliances approved successfully.'
                    : 'Compliances rejected successfully.'
            );
        },
        function(error: any) {
            var data = (error && error.response && error.response.data) || {};
            toast.error(data.error || data.message || 'Bulk review failed.');
        }
    );

    function clearAll() {
        setStatus('');
        setAuditRemarks('');
    }

    function handleSubmit() {
        var eligibleItems = (selectedItems || []).filter(function(item: any) {
            return REVIEWABLE_STATUSES.indexOf(item.status) !== -1;
        });
        if (eligibleItems.length === 0) {
            toast.warning('None of the selected compliances are in Approval Pending status for review.');
            return;
        }
        bulkReviewCompliances({
            compliance_ids: eligibleItems.map(function(item: any) { return item.id; }),
            status: status,
            audit_remarks: auditRemarks.trim(),
        });
    }

    var canSubmit = Boolean(status) && Boolean(auditRemarks.trim());

    return (
        <>
            <div className="cbm-overlay">
                <div className="cbm-modal">

                    {/* Header */}
                    <div className="cbm-header">
                        <h2 className="cbm-title">Bulk Review</h2>
                        <button className="cbm-close" onClick={function() { onClose(); }} title="Close">
                            <IconClose />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="cbm-body">

                        {/* Status selection */}
                        <div className="cbm-field">
                            <label className="cbm-label">
                                Compliance Status <span className="cbm-required">*</span>
                            </label>
                            <div className="cbm-status-opts">

                                {/* Approve card */}
                                <button
                                    type="button"
                                    className={'cbm-status-opt' + (status === ComplianceActivityStatus.APPROVED ? ' cbm-opt-approved' : '')}
                                    onClick={function() { setStatus(ComplianceActivityStatus.APPROVED); }}
                                >
                                    <span className={'cbm-status-dot cbm-dot-approved'} />
                                    <span className="cbm-status-opt-label">
                                        {ComplianceStatusMapping[ComplianceActivityStatus.APPROVED]}
                                    </span>
                                    {status === ComplianceActivityStatus.APPROVED && (
                                        <span style={{ marginLeft: 'auto', color: '#14ae5c' }}>
                                            <IconCheck />
                                        </span>
                                    )}
                                </button>

                                {/* Reject card */}
                                <button
                                    type="button"
                                    className={'cbm-status-opt' + (status === ComplianceActivityStatus.REJECTED ? ' cbm-opt-rejected' : '')}
                                    onClick={function() { setStatus(ComplianceActivityStatus.REJECTED); }}
                                >
                                    <span className={'cbm-status-dot cbm-dot-rejected'} />
                                    <span className="cbm-status-opt-label">
                                        {ComplianceStatusMapping[ComplianceActivityStatus.REJECTED]}
                                    </span>
                                    {status === ComplianceActivityStatus.REJECTED && (
                                        <span style={{ marginLeft: 'auto', color: '#ec221f' }}>
                                            <IconX />
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="cbm-field">
                            <label className="cbm-label">
                                Remarks <span className="cbm-required">*</span>
                            </label>
                            <textarea
                                className="cbm-textarea"
                                rows={4}
                                placeholder="Enter your review remarks here..."
                                value={auditRemarks}
                                onChange={function(e) { setAuditRemarks(e.target.value); }}
                            />
                        </div>

                        {/* Notice */}
                        <div className="cbm-notice">
                            <IconInfo />
                            Only compliances in <strong>&nbsp;Approval Pending&nbsp;</strong> status will be reviewed.
                        </div>

                    </div>

                    {/* Footer */}
                    <div className="cbm-footer">
                        <button className="cbm-btn cbm-btn-back" onClick={function() { onClose(); }}>
                            Back
                        </button>
                        <div className="cbm-footer-right">
                            <button className="cbm-btn cbm-btn-reset" onClick={clearAll}>
                                Reset
                            </button>
                            <button
                                className="cbm-btn cbm-btn-primary"
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                            >
                                Submit
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {reviewing && <PageLoader />}
        </>
    );
}

export default ComplianceBulkReviewModal;
